import {
  TargetLinkSchema,
  type DraftTargetLink,
  type IdentifierRecord,
  type SectorId,
} from '@myplayer/schema';
import { type AdminUserSession, type WorkspaceOption } from './auth-context.js';
import type { SearchSortOption } from './components/ui/primitives.js';
import {
  callClearRecallFunction,
  callSetRecallFunction,
  type ClearRecallCallablePayload,
  type ClearRecallCallableResult,
  type SetRecallCallablePayload,
  type SetRecallCallableResult,
} from './firebase.js';
import type {
  DuplicateIdentifierConflict,
  ProductFacetCounts,
  ProductSortOption,
} from './products-helpers.js';
import {
  generateGs1QrPrintAssets,
  getQrExportMimeType,
  triggerBrowserDownload,
  type GeneratedGs1QrPrintAssets,
  type Gs1SymbolSpecTableId,
  type QrExportFormat,
} from './qr-generator.js';

export type ProductStatusFilter =
  'ALL' | 'LIVE' | 'SCHEDULED' | 'NEEDS_ATTENTION' | 'DRAFT' | 'PENDING' | 'INVALID' | 'RECALLED';
export type ProductValidationFilter = 'ALL' | 'VALID' | 'PENDING' | 'INVALID' | 'WARNING';

export const PRODUCT_SORT_OPTIONS: readonly SearchSortOption[] = [
  { value: 'MODIFIED_DESC', label: 'Modified (Newest first)' },
  { value: 'MODIFIED_ASC', label: 'Modified (Oldest first)' },
  { value: 'TITLE_ASC', label: 'Product (A–Z)' },
  { value: 'TITLE_DESC', label: 'Product (Z–A)' },
  { value: 'STATUS', label: 'Status' },
];

export const PRODUCT_STATUS_FILTER_OPTIONS = [
  ['ALL', 'All statuses'],
  ['LIVE', 'Live'],
  ['SCHEDULED', 'Scheduled'],
  ['NEEDS_ATTENTION', 'Needs attention'],
] as const;

export const PRODUCT_VALIDATION_FILTER_OPTIONS = [
  ['ALL', 'All validation states'],
  ['VALID', 'Valid'],
  ['PENDING', 'Pending'],
  ['INVALID', 'Invalid'],
  ['WARNING', 'Warning'],
] as const;

export interface ProductsTableProps {
  readonly records: readonly IdentifierRecord[];
  readonly selectedIdentifierKey: string | undefined;
  readonly searchQuery: string;
  readonly setSearchQuery: (q: string) => void;
  readonly brandFilter?: string | undefined;
  readonly setBrandFilter?: ((brand: string) => void) | undefined;
  readonly statusFilter: ProductStatusFilter;
  readonly setStatusFilter: (status: ProductStatusFilter) => void;
  readonly sectorFilter: 'ALL' | SectorId;
  readonly setSectorFilter: (sector: 'ALL' | SectorId) => void;
  readonly validationFilter: ProductValidationFilter;
  readonly setValidationFilter: (val: ProductValidationFilter) => void;
  readonly workspaceFilter?: string | undefined;
  readonly setWorkspaceFilter?: ((wsId: string) => void) | undefined;
  readonly availableWorkspaces?: readonly WorkspaceOption[] | undefined;
  readonly workspaceNameMap?: ReadonlyMap<string, string> | undefined;
  readonly showWorkspaceBadges?: boolean | undefined;
  readonly sortBy?: ProductSortOption | undefined;
  readonly setSortBy?: ((sort: ProductSortOption) => void) | undefined;
  readonly viewMode?: 'table' | 'cards' | undefined;
  readonly setViewMode?: ((mode: 'table' | 'cards') => void) | undefined;
  readonly facetCounts?: ProductFacetCounts | undefined;
  readonly onSelectRecord: (identifierKey: string) => void;
  readonly onEditRecord?: ((identifierKey: string) => void) | undefined;
  readonly effectiveResolverHost?: string | undefined;
  readonly canEdit?: boolean | undefined;
  readonly showSearchBar?: boolean;
  readonly defaultPageSize?: number;
  readonly loading?: boolean;
  readonly duplicateConflicts?: ReadonlyMap<string, DuplicateIdentifierConflict> | undefined;
}

export function computeProductQrPreview(
  selectedRecord: IdentifierRecord | null,
  effectiveResolverHost: string,
  qrTableId: Gs1SymbolSpecTableId,
): GeneratedGs1QrPrintAssets | null {
  if (!selectedRecord || effectiveResolverHost.length === 0) return null;
  try {
    return generateGs1QrPrintAssets({
      host: effectiveResolverHost,
      ai: selectedRecord.ai,
      primaryKey: selectedRecord.primaryKey,
      tableId: qrTableId,
    });
  } catch {
    return null;
  }
}

export function downloadProductQrAsset(params: {
  readonly selectedRecord: IdentifierRecord | null;
  readonly effectiveResolverHost: string;
  readonly qrTableId: Gs1SymbolSpecTableId;
  readonly format: QrExportFormat;
  readonly xDimensionMm?: number | undefined;
  readonly onDownloaded: (payload: {
    readonly format: QrExportFormat;
    readonly assets: GeneratedGs1QrPrintAssets;
  }) => void;
}): void {
  if (!params.selectedRecord || params.effectiveResolverHost.length === 0) return;
  const assets = generateGs1QrPrintAssets({
    host: params.effectiveResolverHost,
    ai: params.selectedRecord.ai,
    primaryKey: params.selectedRecord.primaryKey,
    tableId: params.qrTableId,
    ...(params.xDimensionMm !== undefined ? { xDimensionMm: params.xDimensionMm } : {}),
  });
  params.onDownloaded({ format: params.format, assets });
  const safeFilename = `gs1-qr-${params.selectedRecord.identifierKey}-${assets.spec.tableNumber}`;
  const content =
    params.format === 'SVG'
      ? assets.svg
      : params.format === 'PDF'
        ? assets.pdf
        : params.format === 'PNG'
          ? assets.png
          : assets.eps;
  triggerBrowserDownload(
    `${safeFilename}.${params.format.toLowerCase()}`,
    content,
    getQrExportMimeType(params.format),
  );
}

export interface RecallActionOutcome {
  readonly action: 'SET_RECALL' | 'CLEAR_RECALL';
  readonly identifierKey: string;
  readonly status: 'RECALLED' | 'ACTIVE';
  readonly version: number;
  readonly timestamp: string;
  readonly auditId: string;
  readonly reason: string;
  readonly overrideUrl?: string;
}

/**
 * Normalises the client-side recall `overrideUrl` input:
 * - If the user entered a non-empty URL, returns it trimmed.
 * - If the user left it blank and the workspace has a valid `https://` `defaultFallbackUrl` set,
 *   returns that exact value unchanged (never adding `/recall/<key>` or anything else to it).
 * - Otherwise returns `""` (blank) — never inventing any URL.
 */
export function resolveClientRecallOverrideUrl(
  rawOverrideUrl: string,
  workspaceFallbackUrl?: string,
): string {
  const trimmed = rawOverrideUrl.trim();
  if (trimmed.length > 0) {
    return trimmed;
  }
  const trimmedFallback = (workspaceFallbackUrl ?? '').trim();
  if (trimmedFallback.startsWith('https://')) {
    try {
      const parsed = new URL(trimmedFallback);
      if (parsed.protocol === 'https:' && parsed.hostname.length > 0) {
        return trimmedFallback;
      }
    } catch {
      return '';
    }
  }
  return '';
}

export async function confirmProductRecallAction(params: {
  readonly session: AdminUserSession | null;
  readonly selectedRecord: IdentifierRecord | null;
  readonly canRecall: boolean;
  readonly recallReason: string;
  readonly recallOverrideUrl: string;
  readonly workspaceFallbackUrl?: string | undefined;
  readonly effectiveResolverHost?: string | undefined;
  readonly onSetRecall?:
    ((payload: SetRecallCallablePayload) => Promise<SetRecallCallableResult>) | undefined;
  readonly setRecallError: (err: string | null) => void;
  readonly setRecallSubmitting: (v: boolean) => void;
  readonly onRecalled: (updatedLocal: IdentifierRecord, outcome: RecallActionOutcome) => void;
}): Promise<void> {
  if (!params.session || !params.selectedRecord) {
    params.setRecallError('No product selected.');
    return;
  }
  if (!params.canRecall) {
    params.setRecallError(
      'Compliance Officer or Admin role is required to activate a product recall.',
    );
    return;
  }
  const trimmedReason = params.recallReason.trim();
  if (trimmedReason.length === 0) {
    params.setRecallError('A recall reason is required before confirming.');
    return;
  }
  const effectiveOverrideUrl = resolveClientRecallOverrideUrl(
    params.recallOverrideUrl,
    params.workspaceFallbackUrl,
  );
  if (effectiveOverrideUrl.length > 0) {
    let validHttpsUrl = false;
    if (effectiveOverrideUrl.startsWith('https://')) {
      try {
        const parsed = new URL(effectiveOverrideUrl);
        validHttpsUrl = parsed.protocol === 'https:' && parsed.hostname.length > 0;
      } catch {
        validHttpsUrl = false;
      }
    }
    if (!validHttpsUrl) {
      params.setRecallError(
        'Recall Override URL must be a valid https:// URL (or leave blank for the resolver recall notice page).',
      );
      return;
    }
  }
  params.setRecallSubmitting(true);
  try {
    const invokeSetRecall = params.onSetRecall ?? callSetRecallFunction;
    const response = await invokeSetRecall({
      orgId: params.session.orgId,
      workspaceId: params.selectedRecord.workspaceId || params.session.activeWorkspaceId,
      identifierKey: params.selectedRecord.identifierKey,
      reason: trimmedReason,
      overrideUrl: effectiveOverrideUrl,
    });
    const nextVersion = Math.max(response.version, (params.selectedRecord.version ?? 1) + 1);
    const updatedRecord: IdentifierRecord = {
      ...params.selectedRecord,
      status: 'RECALLED',
      version: nextVersion,
      recallOverride: {
        active: true,
        reason: trimmedReason,
        overrideUrl: effectiveOverrideUrl,
        activatedBy: params.session.uid,
        activatedAt: response.activatedAt,
      },
    };
    params.onRecalled(updatedRecord, {
      action: 'SET_RECALL',
      identifierKey: response.identifierKey,
      status: response.status,
      version: nextVersion,
      timestamp: response.activatedAt,
      auditId: response.auditId,
      reason: trimmedReason,
      overrideUrl: effectiveOverrideUrl,
    });
  } catch (err: unknown) {
    params.setRecallError(
      err instanceof Error ? err.message : 'Failed to activate recall override',
    );
  } finally {
    params.setRecallSubmitting(false);
  }
}

export async function clearProductRecallAction(params: {
  readonly session: AdminUserSession | null;
  readonly selectedRecord: IdentifierRecord | null;
  readonly canRecall: boolean;
  readonly effectiveResolverHost?: string | undefined;
  readonly clearReason?: string | undefined;
  readonly onClearRecall?:
    ((payload: ClearRecallCallablePayload) => Promise<ClearRecallCallableResult>) | undefined;
  readonly setRecallError: (err: string | null) => void;
  readonly setRecallSubmitting: (v: boolean) => void;
  readonly onCleared: (updatedLocal: IdentifierRecord, outcome: RecallActionOutcome) => void;
}): Promise<void> {
  if (!params.session || !params.selectedRecord) return;
  if (!params.canRecall) {
    params.setRecallError(
      'Compliance Officer or Admin role is required to clear a product recall.',
    );
    return;
  }
  params.setRecallSubmitting(true);
  try {
    const rec = params.selectedRecord;
    const reasonText =
      typeof params.clearReason === 'string' && params.clearReason.trim().length > 0
        ? params.clearReason.trim()
        : 'Recall cleared via Admin Console';
    const invokeClearRecall = params.onClearRecall ?? callClearRecallFunction;
    const response = await invokeClearRecall({
      orgId: params.session.orgId,
      workspaceId: rec.workspaceId || params.session.activeWorkspaceId,
      identifierKey: rec.identifierKey,
      reason: reasonText,
    });
    const nextVersion = Math.max(response.version, (rec.version ?? 1) + 1);
    const preservedOverrideUrl = resolveClientRecallOverrideUrl(
      rec.recallOverride?.overrideUrl ?? '',
    );
    const updatedRecord: IdentifierRecord = {
      ...rec,
      status: 'ACTIVE',
      version: nextVersion,
      recallOverride: {
        active: false,
        reason: rec.recallOverride?.reason ?? reasonText,
        overrideUrl: preservedOverrideUrl,
        activatedBy: rec.recallOverride?.activatedBy ?? params.session.uid,
        activatedAt: rec.recallOverride?.activatedAt ?? response.deactivatedAt,
      },
    };
    params.onCleared(updatedRecord, {
      action: 'CLEAR_RECALL',
      identifierKey: response.identifierKey,
      status: response.status,
      version: nextVersion,
      timestamp: response.deactivatedAt,
      auditId: response.auditId,
      reason: reasonText,
    });
  } catch (err: unknown) {
    params.setRecallError(err instanceof Error ? err.message : 'Failed to clear recall override');
  } finally {
    params.setRecallSubmitting(false);
  }
}

export function buildUpdatedLinksList(params: {
  readonly workingLinks: readonly DraftTargetLink[];
  readonly editingIndex: number | null;
  readonly linkType: string;
  readonly linkUrl: string;
  readonly linkTitle: string;
  readonly linkLanguage: string;
  readonly linkRegion: string;
  readonly linkIsDefault: boolean;
}):
  | { readonly ok: true; readonly links: readonly DraftTargetLink[] }
  | { readonly ok: false; readonly error: string } {
  const hreflangs = params.linkLanguage
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const contexts = params.linkRegion
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const candidateLink: DraftTargetLink = {
    linkType: params.linkType.trim(),
    href: params.linkUrl.trim(),
    title: params.linkTitle.trim(),
    type: 'text/html',
    hreflang: hreflangs,
    context: contexts.length > 0 ? contexts : ['GLOBAL'],
    defaultLink: params.linkIsDefault,
  };
  const parsedLink = TargetLinkSchema.safeParse(candidateLink);
  if (!parsedLink.success) {
    return {
      ok: false,
      error:
        parsedLink.error.issues[0]?.message ??
        'Link failed validation. Ensure URL starts with https://, title is non-empty, and language is at least 2 characters.',
    };
  }
  const updatedLinks: DraftTargetLink[] = [];
  if (
    params.editingIndex !== null &&
    params.editingIndex >= 0 &&
    params.editingIndex < params.workingLinks.length
  ) {
    for (let i = 0; i < params.workingLinks.length; i += 1) {
      const existing = params.workingLinks[i];
      if (!existing) continue;
      updatedLinks.push(
        i === params.editingIndex
          ? candidateLink
          : candidateLink.defaultLink
            ? { ...existing, defaultLink: false }
            : existing,
      );
    }
  } else {
    const shouldBeDefault =
      candidateLink.defaultLink ||
      params.workingLinks.filter((l) => l.removed !== true).length === 0;
    for (const existing of params.workingLinks) {
      updatedLinks.push(shouldBeDefault ? { ...existing, defaultLink: false } : existing);
    }
    updatedLinks.push({ ...candidateLink, defaultLink: shouldBeDefault });
  }
  return { ok: true, links: updatedLinks };
}
